import React, { useState, useEffect, useContext } from 'react'
import Loader from '../../../../components/FundDetails/Modules/Shared/Loader.jsx'
import ErrorBoundary from '../../../../components/FundDetails/Modules/Performance/ErrorBoundary.jsx'
import { getDownloadCapGains } from '../InvestmentProductsDataSource.js'
import {
  isPresentFundEstimates,
  mapFactsheetUrls,
} from '../helpers/InvestmentProductsHelper.js'
import {
  getAspect,
  getTabData,
  renderColumnCell,
  renderColumnHeader,
  getDataKey,
  sortFunds,
  downloadCapGains,
} from './IntervalFundsHelper.jsx'
import { ProductContext } from '../ProductContext.jsx'
import { MobileFundCard } from '../MobileFundCard.jsx'
import { INTERVAL_FUND_PATH } from '../../../../components/FundDetails/Helpers/FundTypes.js'
import { getTabNames } from '../helpers/InvestmentProductsHelper.js'
import { LayoutContext } from '../../../utility/index.js'

function IntervalFunds() {
  const [funds, setFunds] = useState([])
  const [fundRoot, setFundRoot] = useState({})
  const [selectedSection, setSelectedSection] = useState('')
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [selectedSort, setSelectedSort] = useState('fundName')
  const [selectedSortType, setSelectedSortType] = useState('string')
  const [selectedPerformanceType] = useState('nav')
  const [selectedSortDirection, setSelectedSortDirection] = useState(true)
  const [cusipFactsheetMap, setCusipFactsheetMap] = useState({})
  const { isMobile, tabs, selectedTabKey, changeTab } =
    useContext(ProductContext)
  const layout = useContext(LayoutContext)

  const aspect = getAspect(
    selectedSection,
    selectedSort,
    selectedPerformanceType,
    fundRoot,
    selectedSortDirection,
    cusipFactsheetMap
  )

  useEffect(() => {
    getParentTab()
  }, [])

  useEffect(() => {
    initFundsData()
    if (selectedSection === '#performance') {
      getCusipFactsheetMap()
    }
  }, [selectedSection])

  const getParentTab = async () => {
    const tabData =
      (await getTabNames('interval.funds'))?._embedded?.documents[0]?.bundles
        ?.default || []
    const tabDataKeys = Object.keys(tabData)
    const match = tabDataKeys.find((key) => key.includes(layout.container))
    if (match) {
      setSelectedSection(match.replace(/.*\./, '#'))
    }
  }

  const initFundsData = async () => {
    if (!aspect?.tag) {
      return false
    }
    setIsDataLoading(true)
    const dataKey = getDataKey(selectedSection)
    const data = (await getTabData(aspect?.tag)) || {}
    if (data[dataKey]) {
      setFunds(data[dataKey])
      setFundRoot(data)
    }
    setIsDataLoading(false)
  }

  const onClickSort = (event) => {
    const sort = event.currentTarget.dataset.sort
    const sortType = event.currentTarget.dataset.sortType
    const sortDirection = !!parseInt(event.currentTarget.dataset.sortDirection)

    setSelectedSortType(sortType)

    if (sort === selectedSort) {
      setSelectedSortDirection(!selectedSortDirection)
    } else {
      setSelectedSort(sort)
      setSelectedSortDirection(sortDirection)
    }
  }

  const getCusipFactsheetMap = async () => {
    let data = await mapFactsheetUrls('interval-funds')
    setCusipFactsheetMap(data)
  }

  return (
    <>
      <div className="u-sizeConstrained Content">
        {isMobile && tabs && (
          <div className="Dropdown u-marginVert4gu">
            <label
              className="Form-label u-hiddenVisually"
              htmlFor="productDropdown"
            >
              Data Categories
            </label>
            <select
              className="Dropdown-menu"
              id="productDropdown"
              value={selectedTabKey}
              onChange={(e) => {
                const value = e.target.value
                window.location.hash = value
                changeTab(value)
              }}
            >
              {tabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {isDataLoading && <Loader showBackground={false} />}
        {/* TABLE */}
        {!isDataLoading && funds?.length > 0 && (
          <>
            {/* STATUS / DOWNLOAD */}
            <div className="u-flex u-flexAlignItemsCenter u-marginTop2gu">
              <div className="Form-label u-flexAuto">
                Displaying{' '}
                <span
                  className="u-textBold"
                  data-qa-automation={'displayingFundCount'}
                >
                  {(funds && funds.length) || 0}
                </span>{' '}
                fund(s)
              </div>

              {selectedSection === '#capital-gains' && getDownloadCapGains && (
                <div>
                  <button
                    className="Button Button--tertiary u-padding2gu u-textNoTransform"
                    onClick={(e) =>
                      downloadCapGains(e, 'intervalProductSummary')
                    }
                    data-analytics="download-cap-gains-distributions-landing"
                  >
                    Download Capital Gains Distributions (csv)
                  </button>
                  {isPresentFundEstimates(funds) && (
                    <>
                      {' '}
                      |{' '}
                      <button
                        className="Button Button--tertiary u-padding2gu u-textNoTransform"
                        onClick={(e) =>
                          downloadCapGains(e, 'intervalEstimates')
                        }
                        data-analytics="download-cap-gains-estimates-landing"
                      >
                        Download Capital Gains Estimates (csv)
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {isMobile ? (
              <div className="Accordion">
                {funds.map((fund) => {
                  return (
                    <MobileFundCard
                      key={fund.cusip}
                      fund={fund}
                      product={INTERVAL_FUND_PATH}
                      section={selectedSection}
                      asOfDates={{
                        ytdAsOfDate: fundRoot?.ytdAsOfDate,
                        navAsOfDate: fundRoot?.navAsOfDate,
                        expenseAsOfDate: fundRoot?.expenseAsOfDate,
                        priceAsOfDate: fundRoot?.priceAsOfDate,
                        secYldDate: fundRoot?.secYldDate,
                      }}
                    />
                  )
                })}
              </div>
            ) : (
              <table className="Table Table--reversed Table--sortable u-sm-block u-overflowX InvestmentProductTable">
                <thead
                  className="Table-head is-sticky"
                  style={{
                    top: 0,
                  }}
                >
                  <tr role="row" className="Table-row">
                    <th
                      colSpan={aspect.colSpan1}
                      className="Table-header u-bgColorBrandDark u-alignTop bordered"
                      role="columnheader"
                    ></th>
                    <th
                      colSpan={aspect.colSpan2}
                      className={`Table-header u-bgColorBrandDark u-padding2gu u-alignTop u-textNoWrap ${
                        aspect.colSpan3 && 'bordered'
                      }`}
                      role="columnheader"
                    >
                      {aspect.headerSummary1(funds)}
                    </th>
                    <th
                      colSpan={aspect.colSpan3}
                      className={`Table-header u-bgColorBrandDark u-padding2gu u-alignTop u-textNoWrap ${aspect.colSpan4 ? 'bordered' : ''}`}
                      role="columnheader"
                    >
                      {aspect.headerSummary2()}
                    </th>
                    {aspect.colSpan4 && (
                      <th
                        colSpan={aspect.colSpan4}
                        className="Table-header u-bgColorBrandDark u-padding2gu u-alignTop u-textNoWrap"
                        role="columnheader"
                      ></th>
                    )}
                  </tr>
                  <tr role="row" className="Table-row">
                    {aspect.columns
                      .filter((column) => !column.hide)
                      .map((column) =>
                        renderColumnHeader(
                          column,
                          selectedSortDirection,
                          onClickSort
                        )
                      )}
                  </tr>
                </thead>
                {funds.length < 1 ? (
                  <tbody className="Table-body Table-row">
                    <tr>
                      <td className="Table-cell" colSpan={12}>
                        No matching funds found.
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  sortFunds(
                    funds,
                    selectedSortDirection,
                    selectedSortType,
                    selectedSort
                  ).map((fund, idx) => (
                    <tbody
                      className="Table-body Table-row"
                      key={`${selectedSection}-${idx}`}
                    >
                      <tr role="row">
                        {aspect.columns
                          .filter((column) => !column.hide)
                          .map((column) =>
                            renderColumnCell(
                              {
                                ...column,
                                key: `${selectedSection}-${idx}-${column.name}`,
                              },
                              fund
                            )
                          )}
                      </tr>
                    </tbody>
                  ))
                )}
              </table>
            )}
          </>
        )}
      </div>
      <div id="ampf-trace-loaded-page"></div>
    </>
  )
}

export function IntervalFundsContainer() {
  return (
    <ErrorBoundary
      title="Interval Funds"
      message="Interval Fund information is currently unavailable due to a technical issue. Please try again later."
      isConstrained={true}
    >
      <IntervalFunds />
    </ErrorBoundary>
  )
}
